import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Self-gating, dismissible reminder for APPROVED members whose profile is
// incomplete. Links to the existing /profile/complete wizard (NO hard redirect,
// so members are never trapped). Dismissal is remembered per-user-per-day in
// localStorage, so the nudge recurs daily until the profile is actually done.
// Tolerates both canonical (jobTitle/aboutMe) and legacy (title/bio) keys.
const isProfileComplete = (p) => {
  if (!p) return false;
  if (p.profileCompleted) return true;
  const jobTitle = p.jobTitle || p.title;
  const about = p.aboutMe || p.bio;
  return Boolean(jobTitle && p.company && about && p.city && p.state);
};

const ProfileCompletionBanner = () => {
  const { userProfile, currentUser, isAdmin } = useAuth();

  const dayKey = new Date().toISOString().slice(0, 10);
  const storageKey = currentUser
    ? `apn_profile_banner_dismissed_${currentUser.uid}_${dayKey}`
    : null;

  const [dismissed, setDismissed] = useState(() => {
    try { return storageKey ? localStorage.getItem(storageKey) === '1' : false; }
    catch (e) { return false; }
  });

  const adminUser = typeof isAdmin === 'function' ? isAdmin() : isAdmin;
  const approved = userProfile?.status === 'approved' || userProfile?.role === 'member';

  if (!userProfile || adminUser || !approved) return null;
  if (isProfileComplete(userProfile) || dismissed) return null;

  const dismiss = () => {
    try { if (storageKey) localStorage.setItem(storageKey, '1'); } catch (e) { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div className="mb-6 rounded-lg border border-apn-blue bg-blue-50 p-4 sm:flex sm:items-center sm:justify-between">
      <div className="flex items-start sm:items-center">
        <svg className="h-6 w-6 text-apn-blue flex-shrink-0 mt-0.5 sm:mt-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <div className="ml-3">
          <p className="text-sm font-semibold text-apn-dark-blue">Finish setting up your profile</p>
          <p className="text-sm text-gray-600">
            Add your title, company and a short bio so other members can find and connect with you in the directory.
          </p>
        </div>
      </div>
      <div className="mt-3 sm:mt-0 sm:ml-4 flex items-center gap-2 flex-shrink-0">
        <Link
          to="/profile/complete"
          className="inline-flex items-center px-4 py-2 rounded-md bg-apn-blue text-white text-sm font-medium hover:bg-apn-dark-blue"
        >
          Complete profile
        </Link>
        <button onClick={dismiss} aria-label="Dismiss reminder" className="p-2 text-gray-400 hover:text-gray-600">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ProfileCompletionBanner;
